import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { enqueueSnackbar } from 'notistack';
import { CircularProgress } from '@mui/material';
import CreditsAPI from '../../api/CreditsAPI';
import InsufficientCredits from '../../pages/credit-plans/InsufficientCredits';
import { csvJSON } from '../../utils/CsvToJson';
import * as XLSX from 'xlsx';

// MUI Icons
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';

// Headers that MUST be present for a CSV/XLSX to validate. These match the
// original template exactly so any file uploaded before the SPI Code column
// existed still passes validation.
const REQUIRED_HEADERS = [
  'HTS Code (10 dgit)',
  'Country of Origin (ISO-2)',
  'Product Value',
  'Transportation Method',
  'Entry Date (YYYY-MM-DD)',
  'Loading Date (YYYY-MM-DD)',
  'Country of Smelt / Pour (ISO-2)',
  'Material Percentage (.xx)',
  'Ad Valorem',
  'Use FTA (Y/N)',
  'Product Code',
];

// Full template (what we generate when the user downloads the example).
// Several columns past the REQUIRED set are new and OPTIONAL — old uploads
// omit them and that's fine. When supplied:
//
//   * SPI Code       — free-form text, e.g. "A", "S+", "CL P+".
//   * Aluminum Value — $ amount of aluminum in the product. Percentage is
//                      derived as value / Product Value.
//   * Smelt Country  — country of smelt (pairs with Aluminum Value).
//   * Steel Value    — $ amount of steel.
//   * Pour Country   — country of pour (pairs with Steel Value).
//   * Copper Value   — $ amount of copper.
//
// The five metal columns are inserted right after "Material Percentage"
// so they extend the metals/material section of the template naturally.
const METAL_TEMPLATE_HEADERS = [
  'Aluminum Value',
  'Smelt Country',
  'Steel Value',
  'Pour Country',
  'Copper Value',
];

// The reported quantity and the unit it was reported in. Optional like the
// metal columns, but supply them whenever the HTS carries a specific
// (per-unit) duty component: "2.3% + $1.05/kg" is charged on the kilograms,
// and with no quantity BITE can only price the ad valorem half and flags the
// line "Specific Rate Missing Unit Quantity". Unit of Measure is free text —
// "KG", "NO", "DOZ", "PRS", "LITER" — folded to the calculator's canonical
// units at run time. This is NOT the "Ad Valorem" column, which no duty math
// reads.
const QUANTITY_TEMPLATE_HEADERS = ['Quantity', 'Unit of Measure'];

const TEMPLATE_HEADERS = [
  ...REQUIRED_HEADERS.slice(
    0,
    REQUIRED_HEADERS.indexOf('Material Percentage (.xx)') + 1,
  ),
  ...METAL_TEMPLATE_HEADERS,
  ...QUANTITY_TEMPLATE_HEADERS,
  ...REQUIRED_HEADERS.slice(
    REQUIRED_HEADERS.indexOf('Material Percentage (.xx)') + 1,
    REQUIRED_HEADERS.indexOf('Product Code'),
  ),
  'SPI Code',
  'Product Code',
];

// Helper to normalize headers for comparison (removes spaces, special characters, uppercase)
const normalizeHeader = (header: string) =>
  header
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const NORMALIZED_REQUIRED = REQUIRED_HEADERS.map(normalizeHeader);

// --- Billing -------------------------------------------------------------
// The Tariff Analyzer bills one analysis credit per block of rows rather than
// per row: any partial block rounds up, so 5 rows cost 1 credit and 6 cost 2.
// The authoritative charge is made server-side when the run finalizes, in
// automations/src/jobs/common/tariff.chunk.processor.ts. That file holds the
// same divisor -- change both together or this quote disagrees with the bill.
export const TARIFF_ROWS_PER_CREDIT = 5;

export const creditsForTariffRows = (rowCount: number): number =>
  rowCount > 0 ? Math.ceil(rowCount / TARIFF_ROWS_PER_CREDIT) : 0;

// Counts DATA rows -- both parsers below drop the header row. This is an upper
// bound on what the run actually costs: the server bills the rows it manages to
// persist, and rows the importer rejects as invalid never reach it. Erring high
// is deliberate, so the pre-flight check never waves through a run the team
// cannot afford.
const getUploadRowCount = async (file: File): Promise<number> => {
  // Case-insensitive: the dropzone's own validator lowercases before checking
  // the extension, so DATA.CSV / DATA.XLSX are accepted uploads and must be
  // counted here too.
  const fileName = file.name.toLowerCase();

  if (fileName.endsWith('.csv') || file.type === 'text/csv') {
    const text = await file.text();
    const rows = csvJSON(text);
    return Array.isArray(rows) ? rows.length : 0;
  }

  if (fileName.endsWith('.xlsx')) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
    return Array.isArray(rows) ? rows.length : 0;
  }

  // Fail CLOSED. Returning 0 here quoted a zero-credit run and waved the file
  // straight past the pre-flight balance check; the server would then bill the
  // real row count and the spend could fail after the analysis had already run.
  throw new Error('Unsupported tariff upload format.');
};

interface RunTariffAnalysisModalProps {
  open: boolean;
  handleClose: () => void;
  onAnalyze: (
    name: string,
    file: File,
    spendMode: 'trial' | 'active',
  ) => Promise<void>;
  cur_user: any;
  isWhiteLabel?: boolean;
}

interface TariffCreditBalances {
  trial: number;
  paid: number;
  available: number;
}

const getTariffCreditBalances = async (
  teamId: string,
): Promise<TariffCreditBalances> => {
  const [paidResponse, trialResponse] = await Promise.all([
    CreditsAPI.getCreditsSummary(teamId),
    CreditsAPI.getTrialCreditsSummary(teamId),
  ]);

  if (!paidResponse?.success || !trialResponse?.success) {
    throw new Error('Unable to validate available analysis credits.');
  }

  const trialSummary = Array.isArray(trialResponse.result)
    ? trialResponse.result[0]
    : trialResponse.result;
  const rawTrial = Number(trialSummary?.remaining_credits ?? 0);
  const rawPaid = Number(paidResponse.result?.remaining_credits ?? 0);
  const trial = Number.isFinite(rawTrial) ? Math.max(rawTrial, 0) : 0;
  const paid = Number.isFinite(rawPaid) ? Math.max(rawPaid, 0) : 0;

  // A run is never split between pools. The largest single balance is the
  // amount that can cover one run; trial remains preferred when it covers the
  // complete charge, otherwise the paid pool is used.
  return { trial, paid, available: Math.max(trial, paid) };
};

const RunTariffAnalysisModal: React.FC<RunTariffAnalysisModalProps> = ({
  open,
  handleClose,
  onAnalyze,
  cur_user,
  isWhiteLabel = false,
}) => {
  const [analysisName, setAnalysisName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);
  const [requiredAnalysisCredits, setRequiredAnalysisCredits] = useState(0);
  const [displaySpendMode, setDisplaySpendMode] = useState<'trial' | 'active'>(
    'active',
  );
  // Row count of the accepted file, taken at drop time so the cost of the run
  // is on screen before the user commits to it. null = not counted yet, or the
  // count failed; the cost panel degrades to just the rate in that case.
  const [uploadRowCount, setUploadRowCount] = useState<number | null>(null);
  const [isCountingRows, setIsCountingRows] = useState(false);
  // Identifies the in-flight count so a slow count of a file the user has
  // already replaced can't overwrite the newer file's figure.
  const rowCountRequestId = useRef(0);
  // Team balance, for display only. handleSubmit refetches before it decides,
  // so a stale value here can never let an unaffordable run through.
  const [availableCredits, setAvailableCredits] = useState<number | null>(null);

  const teamId = cur_user?.selected_team?.id;

  useEffect(() => {
    // Drop the old figure before fetching, and keep it dropped when there is no
    // team. TariffAnalyzers renders this modal unconditionally (`open` only
    // gates the render), so state survives a close -- without this, reopening
    // after a team switch shows the previous team's balance until the new fetch
    // lands, and computes the shortfall against it.
    setAvailableCredits(null);

    if (!open || !teamId || isWhiteLabel) return undefined;

    let cancelled = false;
    getTariffCreditBalances(teamId)
      .then((balances) => {
        if (!cancelled) {
          setAvailableCredits(balances.available);
          setDisplaySpendMode(
            balances.trial >= balances.paid ? 'trial' : 'active',
          );
        }
      })
      .catch(() => {
        // Non-fatal: the panel simply omits the balance line.
      });

    return () => {
      cancelled = true;
    };
  }, [open, teamId, isWhiteLabel]);

  // Clears the file and everything derived from it. Bumping the request id is
  // the load-bearing part: a count still in flight for the file being cleared
  // would otherwise resolve after the reset and leave a row count -- and so a
  // credit estimate and a possible "not enough credits" warning -- on screen
  // with no file selected, persisting into the next open.
  const clearSelectedFile = useCallback(() => {
    rowCountRequestId.current += 1;
    setSelectedFile(null);
    setUploadRowCount(null);
    setIsCountingRows(false);
  }, []);

  // Generates and downloads the empty CSV template for the user. Template
  // includes the optional SPI Code column so users see it in the example;
  // it is NOT required for upload validation (REQUIRED_HEADERS controls that).
  const handleDownloadTemplate = () => {
    const csvContent = `${TEMPLATE_HEADERS.map((h) => `"${h}"`).join(',')}\n`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'Tariff_Analyzer_Template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // CSV & XLSX Validation Logic
  const validateFile = async (
    file: File,
  ): Promise<{ isValid: boolean; missing: string[] }> => {
    return new Promise(async (resolve) => {
      // Only the REQUIRED_HEADERS set is enforced. SPI Code (new) and any
      // future optional columns are silently accepted when present and
      // silently ignored when absent so existing uploads never break.
      const checkHeaders = (fileHeaders: string[]) => {
        const missing: string[] = [];
        REQUIRED_HEADERS.forEach((expected, index) => {
          if (!fileHeaders.includes(NORMALIZED_REQUIRED[index])) {
            missing.push(expected);
          }
        });
        return missing;
      };

      // Handle XLSX logic
      if (file.name.toLowerCase().endsWith('.xlsx')) {
        try {
          const buffer = await file.arrayBuffer();
          const workbook = XLSX.read(buffer, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });

          if (rows.length > 0) {
            const fileHeaders = (rows[0] as any[]).map((h) =>
              normalizeHeader(String(h || '')),
            );
            const missing = checkHeaders(fileHeaders);
            resolve({ isValid: missing.length === 0, missing: missing });
          } else {
            resolve({ isValid: false, missing: REQUIRED_HEADERS });
          }
        } catch (err) {
          resolve({ isValid: false, missing: ['File is corrupted or empty'] });
        }
        return;
      }

      // Standard CSV parsing
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        // Robust split handles Windows (\r\n), Mac (\r), and Linux (\n)
        const firstLine = text.split(/\r\n|\n|\r/)[0];

        if (!firstLine) {
          resolve({ isValid: false, missing: REQUIRED_HEADERS });
          return;
        }

        const fileHeaders = firstLine.split(',').map(normalizeHeader);
        const missing = checkHeaders(fileHeaders);
        resolve({ isValid: missing.length === 0, missing: missing });
      };

      reader.onerror = () =>
        resolve({ isValid: false, missing: ['Error reading file'] });
      reader.readAsText(file.slice(0, 2048)); // Read first 2KB for headers
    });
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[], fileRejections: any[]) => {
      setUploadError(''); // Clear previous errors

      if (fileRejections.length > 0) {
        setUploadError(
          'Invalid file type. Please upload a .csv or .xlsx file.',
        );
        clearSelectedFile();
        return;
      }

      const file = acceptedFiles[0];
      if (!file) return;

      const { isValid, missing } = await validateFile(file);

      if (isValid) {
        setSelectedFile(file);

        // Count now, not at submit, so the credit cost is visible while the
        // user can still swap the file for a smaller one.
        rowCountRequestId.current += 1;
        const requestId = rowCountRequestId.current;
        setUploadRowCount(null);
        setIsCountingRows(true);
        try {
          const count = await getUploadRowCount(file);
          if (rowCountRequestId.current !== requestId) return;
          setUploadRowCount(count);
        } catch (err) {
          // Leave it null. handleSubmit recounts before charging, so a failed
          // count here costs the preview, not the guard.
          if (rowCountRequestId.current !== requestId) return;
          setUploadRowCount(null);
        } finally {
          if (rowCountRequestId.current === requestId) {
            setIsCountingRows(false);
          }
        }
      } else {
        clearSelectedFile();
        setUploadError(`Missing template headers: ${missing.join(', ')}`);
        enqueueSnackbar('Invalid document format. Headers do not match.', {
          variant: 'error',
        });
      }
    },
    [clearSelectedFile],
  );

  const fileValidator = (currentFile: File) => {
    const acceptedExtensions = ['.csv', '.xlsx'];
    const fileExtension = `.${currentFile.name
      .split('.')
      .pop()
      ?.toLowerCase()}`;

    if (!acceptedExtensions.includes(fileExtension)) {
      return {
        code: 'file-type-invalid',
        message: 'File type must be .csv or .xlsx',
      };
    }
    return null;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    multiple: false,
    // Note: Deliberately omitting the `accept` prop.
    // We let fileValidator handle it to prevent react-dropzone from silently rejecting valid OS mime-types.
  });

  const handleSubmit = async () => {
    if (!analysisName.trim() || !selectedFile) return;

    setIsSubmitting(true);
    try {
      if (!isWhiteLabel && teamId) {
        const [rowCount, balances] = await Promise.all([
          // Reuse the drop-time count when we have it; recount only if that
          // pass failed, so the guard never runs on a missing row count.
          uploadRowCount === null
            ? getUploadRowCount(selectedFile)
            : Promise.resolve(uploadRowCount),
          getTariffCreditBalances(teamId),
        ]);

        // Credits, not rows: one credit buys TARIFF_ROWS_PER_CREDIT rows and
        // the remainder rounds up.
        const requiredCredits = creditsForTariffRows(rowCount);
        setUploadRowCount(rowCount);
        setRequiredAnalysisCredits(requiredCredits);

        const availableAnalysisCredits = balances.available;
        setAvailableCredits(availableAnalysisCredits);

        if (requiredCredits > availableAnalysisCredits) {
          setDisplaySpendMode(
            balances.trial >= balances.paid ? 'trial' : 'active',
          );
          setShowInsufficientCredits(true);
          return;
        }

        const spendMode =
          balances.trial >= requiredCredits ? 'trial' : 'active';
        setDisplaySpendMode(spendMode);
        await onAnalyze(analysisName, selectedFile, spendMode);
      } else {
        await onAnalyze(analysisName, selectedFile, 'active');
      }

      setAnalysisName('');
      clearSelectedFile();
      setUploadError('');
      handleClose();

      enqueueSnackbar('Analysis started successfully.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to start analysis. Please try again.', {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetAndClose = () => {
    setAnalysisName('');
    clearSelectedFile();
    setUploadError('');
    handleClose();
  };

  const isReadyToSubmit =
    analysisName.trim().length > 0 && selectedFile !== null && !isSubmitting;

  const hasRowCount = uploadRowCount !== null && !isCountingRows;
  const estimatedCredits =
    uploadRowCount !== null && !isCountingRows
      ? creditsForTariffRows(uploadRowCount)
      : 0;
  // Only claim a shortfall once both numbers are known -- an unread balance
  // must not look like an empty one. Submitting anyway is still allowed: the
  // guard in handleSubmit opens the InsufficientCredits modal, which is where
  // the user tops up.
  const creditShortfall =
    hasRowCount && availableCredits !== null
      ? Math.max(estimatedCredits - availableCredits, 0)
      : 0;

  if (!open) return null;

  return (
    <div
      className='modal-overlay'
      onClick={handleResetAndClose}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        className='modal-cont'
        style={{
          width: '540px',
          maxWidth: '90vw',
          // The credit panel pushes the empty dialog to ~690px. .modal-overlay
          // is a fixed, unscrollable 100vh box and .modal-cont sets no
          // max-height, so on a short viewport (~1280x800 laptop, or mobile
          // landscape) the footer would sit off-screen with no way to reach it.
          // Cap the dialog and let the body scroll instead.
          maxHeight: '90vh',
          padding: 0,
          position: 'relative',
          top: 'auto',
          left: 'auto',
          transform: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className='modal-header-cont-temp'
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--es-soft-border)',
            width: '100%',
            // Header and footer stay put; only the body between them scrolls.
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            <div className='modal-header-cont-text'>
              <h1
                className='modal-header-cont-text-h1'
                style={{ fontSize: '20px', marginBottom: '4px' }}
              >
                Run New Analysis
              </h1>
              <p className='modal-header-cont-text-p' style={{ margin: 0 }}>
                Upload a document to analyze and calculate your tariffs.
              </p>
            </div>
            <div
              className='module-header-btn-close'
              onClick={handleResetAndClose}
              style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
            >
              <CloseIcon />
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          className='modal-body'
          style={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            width: '100%',
            boxSizing: 'border-box',
            overflowY: 'auto',
            // A flex child will not shrink below its content without this, so
            // overflowY alone would not produce a scrollbar.
            minHeight: 0,
          }}
        >
          {/* Analysis Name Input */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <label
              style={{
                color: 'var(--text-primary-color)',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Analysis Name
            </label>
            <input
              type='text'
              value={analysisName}
              onChange={(e) => setAnalysisName(e.target.value)}
              placeholder='e.g. Q4 2024 Tariffs'
              style={{
                background: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                border: '1px solid var(--es-soft-border)',
                padding: '12px 14px',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Dropzone Area */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
              }}
            >
              <label
                style={{
                  color: 'var(--text-primary-color)',
                  fontSize: '14px',
                  fontWeight: 500,
                  margin: 0,
                }}
              >
                Tariff Document (CSV / XLSX)
              </label>
              <span
                onClick={handleDownloadTemplate}
                style={{
                  color: '#4895ef',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <FileDownloadOutlinedIcon style={{ fontSize: 16 }} /> Download
                Template
              </span>
            </div>

            {!selectedFile ? (
              <div
                {...getRootProps()}
                style={{
                  border: `2px dashed ${
                    isDragActive ? '#4895ef' : 'var(--es-soft-border)'
                  }`,
                  borderRadius: '8px',
                  padding: '40px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: isDragActive
                    ? 'rgba(72, 149, 239, 0.05)'
                    : 'var(--bg-secondary)',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadOutlinedIcon
                  style={{
                    fontSize: 44,
                    color: 'var(--text-secondary)',
                    marginBottom: '12px',
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    color: 'var(--text-primary-color)',
                    fontWeight: 500,
                    fontSize: '14px',
                  }}
                >
                  <span style={{ color: '#4895ef' }}>Click to upload</span> or
                  drag and drop
                </p>
                <p
                  style={{
                    margin: '6px 0 0 0',
                    color: 'var(--text-secondary)',
                    fontSize: '13px',
                  }}
                >
                  CSV or XLSX files only
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  border: '1px solid var(--es-soft-border)',
                  borderRadius: '8px',
                  background: 'var(--bg-secondary)',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <InsertDriveFileOutlinedIcon
                    style={{ color: '#4895ef', fontSize: 28 }}
                  />
                  <div>
                    <p
                      style={{
                        margin: 0,
                        color: 'var(--text-primary-color)',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {selectedFile.name}
                    </p>
                    <p
                      style={{
                        margin: '2px 0 0 0',
                        color: 'var(--text-secondary)',
                        fontSize: '13px',
                      }}
                    >
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <div
                  onClick={clearSelectedFile}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    color: '#f04438',
                  }}
                >
                  <DeleteOutlineIcon fontSize='small' />
                </div>
              </div>
            )}

            {/* Display validation errors here so they don't get hidden behind the modal */}
            {uploadError ? (
              <p
                style={{
                  margin: '4px 0 0 0',
                  color: '#f04438',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                {uploadError}
              </p>
            ) : (
              <p
                style={{
                  margin: '4px 0 0 0',
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                  fontStyle: 'italic',
                }}
              >
                * Only documents containing the template headers are accepted.
              </p>
            )}
          </div>

          {/* Credit cost. Billing is per block of rows, not per run, so the
              rate, this file's estimate, and any shortfall all belong on
              screen before the user commits. */}
          {!isWhiteLabel && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid var(--es-soft-border)',
              background: 'var(--bg-surface)',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TollOutlinedIcon
                style={{ fontSize: 18, color: 'var(--action-color)' }}
              />
              <span
                style={{
                  color: 'var(--text-primary-color)',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Credit cost
              </span>
              <span
                style={{
                  marginLeft: 'auto',
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                }}
              >
                1 credit per {TARIFF_ROWS_PER_CREDIT} rows
              </span>
            </div>

            {selectedFile ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '12px',
                }}
              >
                <span
                  style={{ color: 'var(--text-secondary)', fontSize: '13px' }}
                >
                  {isCountingRows
                    ? 'Counting rows…'
                    : uploadRowCount === null
                    ? 'Row count unavailable — the cost is calculated when you run the analysis.'
                    : `${uploadRowCount.toLocaleString()} ${
                        uploadRowCount === 1 ? 'row' : 'rows'
                      } detected`}
                </span>
                {hasRowCount && (
                  <span
                    style={{
                      fontFamily: 'Inter, sans-serif',
                      fontVariantNumeric: 'tabular-nums',
                      color: 'var(--text-primary-color)',
                      fontSize: '15px',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {estimatedCredits.toLocaleString()}{' '}
                    {estimatedCredits === 1 ? 'credit' : 'credits'}
                  </span>
                )}
              </div>
            ) : (
              <span
                style={{ color: 'var(--text-secondary)', fontSize: '13px' }}
              >
                Upload a document to see what this run costs. Partial blocks
                round up, so {TARIFF_ROWS_PER_CREDIT + 1} rows cost 2 credits.
              </span>
            )}

            {availableCredits !== null && (
              <span
                style={{
                  fontSize: '13px',
                  color: creditShortfall
                    ? 'var(--low-color)'
                    : 'var(--text-tertiary)',
                }}
              >
                {creditShortfall
                  ? `Not enough credits — this run needs ${creditShortfall.toLocaleString()} more than the ${availableCredits.toLocaleString()} remaining.`
                  : `${availableCredits.toLocaleString()} ${
                      availableCredits === 1 ? 'credit' : 'credits'
                    } remaining.`}
              </span>
            )}
          </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid var(--es-soft-border)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <button
            className='btn-outline'
            onClick={handleResetAndClose}
            disabled={isSubmitting}
            style={{ margin: 0, width: 'auto' }}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            disabled={!isReadyToSubmit}
            onClick={handleSubmit}
            style={{
              margin: 0,
              width: 'auto',
              opacity: isReadyToSubmit ? 1 : 0.5,
              cursor: isReadyToSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              'Analyze Document'
            )}
          </button>
        </div>
      </div>
      <InsufficientCredits
        teamId={cur_user?.selected_team?.id || ''}
        type='analysis'
        requiredCredits={requiredAnalysisCredits}
        isOpen={showInsufficientCredits}
        onClose={() => setShowInsufficientCredits(false)}
        isTrialing={displaySpendMode === 'trial'}
      />
    </div>
  );
};

export default RunTariffAnalysisModal;
